import React from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Toaster } from 'react-hot-toast'
import { RootState } from '../../store'
import { useTheme } from '../../hooks'
import Header from './Header'
import Sidebar from './Sidebar'
import LoadingSpinner from '../ui/LoadingSpinner'
import ErrorBoundary from '../ui/ErrorBoundary'
import { PauseIcon, PlayIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { tickTechnique, pauseTechnique, resumeTechnique, setShowOverlay } from '../../store/slices/timeTechniqueSlice'
import { ROUTES } from '../../utils/constants'

interface LayoutProps {
  children?: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { loading } = useSelector((state: RootState) => state.ui)
  const location = useLocation()
  const isFullBleedRoute = ['/tools/mindmaps'].some(path => location.pathname.startsWith(path))
  const { sidebarOpen } = useTheme()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isActive, isBreak, timeLeft, started, showOverlay, technique } = useSelector((state: RootState) => state.timeTechnique)

  // Background ticker: keep timer progressing even when navigating between pages
  React.useEffect(() => {
    if (!started) return
    const id = setInterval(() => {
      dispatch(tickTechnique())
    }, 1000)
    return () => clearInterval(id)
  }, [started, dispatch])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen overflow-x-hidden bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
        {/* Sidebar */}
        <Sidebar />
        
        {/* Main Content */}
        <div className={`flex flex-col min-h-screen transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-0'}`}>
          {/* Header */}
          <Header />
          
          {/* Page Content */}
          <main className="flex-1 relative pt-16">
            {/* Background Pattern */}
            <div className="absolute inset-0 -z-10 bg-gradient-to-br from-blue-50/50 via-white to-purple-50/50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800" />
            
            {/* Content Container */}
            <div className={`relative ${isFullBleedRoute ? 'p-0' : 'p-4 lg:p-6'}`}>
              {children || <Outlet />}
            </div>
            
            {/* Global Loading Overlay */}
            {loading && (
              <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-50 flex items-center justify-center">
                <LoadingSpinner size="lg" />
              </div>
            )}

            {/* Persistent Timer Overlay */}
            {showOverlay && technique && (
              <div className="fixed bottom-4 right-4 z-40">
                <div className="group flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 backdrop-blur supports-[backdrop-filter]:bg-white/70 dark:supports-[backdrop-filter]:bg-gray-800/70">
                  <div className={`w-2 h-2 rounded-full ${isBreak ? 'bg-green-500' : 'bg-red-500'}`} title={isBreak ? 'Mola' : 'Çalışma'} />
                  <button
                    onClick={() => navigate(ROUTES.POMODORO)}
                    className="text-sm font-medium text-gray-900 dark:text-white hover:underline"
                    title="Süre Teknikleri sayfasına git"
                  >
                    {technique.name}
                  </button>
                  <span className="font-mono text-sm tabular-nums text-gray-900 dark:text-white">
                    {formatTime(timeLeft)}
                  </span>
                  <button
                    onClick={() => dispatch(isActive ? pauseTechnique() : resumeTechnique())}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                    title={isActive ? 'Durdur' : 'Başlat'}
                  >
                    {isActive ? <PauseIcon className="w-5 h-5" /> : <PlayIcon className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => dispatch(setShowOverlay(false))}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                    title="Sayaçı gizle"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Toaster */}
            <Toaster position="top-right" />
          </main>
        </div>
      </div>
    </ErrorBoundary>
  )
}

export default Layout